import { ApiHttpError, getCurrentUser } from '@avila-roffe/api-clients-admin-bff';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { useTenantConfig } from '../tenant/useTenantConfig';

interface ProfileResponse {
  data?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    avatarUrl?: string;
  };
}

export interface CurrentUser {
  userId: string;
  email: string;
  displayName: string;
  initials: string;
  tenantSlug: string;
  roles: string[];
  avatarUrl?: string;
}

function buildInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function useCurrentUser(): CurrentUser | null {
  const { user } = useAuth();
  const { slug } = useTenantConfig();

  const { data } = useQuery<ProfileResponse>({
    queryKey: ['me', 'profile', slug],
    queryFn: async () => {
      try {
        return (await getCurrentUser(slug)) as Promise<ProfileResponse>;
      } catch (err) {
        // Profile is optional enrichment — silently ignore 404 (route not deployed) and 403 (role gap)
        if (err instanceof ApiHttpError && (err.status === 404 || err.status === 403)) return null;
        throw err;
      }
    },
    enabled: !!user && !!slug,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!user) return null;

  const firstName = data?.data?.firstName ?? '';
  const lastName = data?.data?.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const displayName = user.displayName || fullName || user.email;

  return {
    userId: user.id,
    email: user.email,
    displayName,
    initials: buildInitials(displayName),
    tenantSlug: user.tenantSlug,
    roles: user.roles ?? [],
    avatarUrl: data?.data?.avatarUrl,
  };
}
